import React, { useContext, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import AuthContext from '../../../context/authContext';
import useInternalBooking from '../../../hooks/useInternalBooking';
import { hasNoShowInvoiceBillableLineItems } from '../../../util/dateUtils';
import LockIconWrapper from '../../components/LockIconWrapper';
import { hasNoShowInvoiceAdyenMerchant } from './NoShowInvoiceButton';

function SkipNoShowInvoiceButton({
  booking,
  onBookingUpdated,
  onAfterSkip,
  variant = 'outlined',
  size = 'small',
  fullWidth = false,
  sx = undefined,
}) {
  const authCtx = useContext(AuthContext);
  const { skipNoShowInvoice } = useInternalBooking();
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canShow = useMemo(() => {
    if (!booking?._id) return false;
    if (booking.status !== 'noShow') return false;
    if (!hasNoShowInvoiceAdyenMerchant(booking)) return false;
    if (booking.noShowInvoiceId || booking.noShowInvoiceSkippedAt) return false;
    if (!hasNoShowInvoiceBillableLineItems(booking)) return false;
    return authCtx.checkRightAny('internalBooking:special:sendNoShowInvoice');
  }, [authCtx, booking]);

  if (!canShow) return null;

  const handleOpen = event => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    setOpen(true);
  };

  const handleClose = event => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (isSubmitting) return;
    setOpen(false);
  };

  const handleSubmit = async event => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (!booking?._id) return;

    try {
      setIsSubmitting(true);
      const response = await skipNoShowInvoice(booking._id);
      if (!response) return;
      if (response?.booking) {
        onBookingUpdated?.(booking._id, response.booking);
      }
      toast.success('No-Show-Rechnung als erledigt markiert');
      setOpen(false);
      await onAfterSkip?.();
    } catch (err) {
      if (err?.message !== 'INSUFFICIENT_RIGHTS') {
        toast.error('Fehler beim Abschließen der No-Show-Rechnung');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button
        size={size}
        variant={variant}
        color='inherit'
        fullWidth={fullWidth}
        onClick={handleOpen}
        data-ignore-card-click
        sx={sx}
      >
        <LockIconWrapper right='internalBooking:special:sendNoShowInvoice'>
          No-Show-Rechnung nicht versenden (erledigt)
        </LockIconWrapper>
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth='xs' fullWidth>
        <DialogTitle>No-Show-Rechnung nicht versenden?</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary'>
            Die Buchung verschwindet aus dem Handlungsbedarf. Es wird keine No-Show-Rechnung
            erstellt oder versendet.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            color='warning'
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            Als erledigt markieren
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default SkipNoShowInvoiceButton;
